import React, { useState } from 'react';
import { Image, Link as LinkIcon, Hash, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from '../Modal';
import ActionIcon from '../common/ActionIcon';
import TagInput from '../forms/TagInput';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (post: {
    content: string;
    attachments: { type: 'image' | 'link'; url: string; preview?: string }[];
    tags: string[];
  }) => void;
}

export default function CreatePostModal({ isOpen, onClose, onSubmit }: CreatePostModalProps) {
  const { theme } = useTheme();
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<{ type: 'image' | 'link'; url: string; preview?: string }[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPreview, setLinkPreview] = useState('');

  const handleSubmit = () => {
    if (content.trim()) {
      onSubmit({
        content: content.trim(),
        attachments,
        tags
      });
      handleReset();
    }
  };

  const handleReset = () => {
    setContent('');
    setAttachments([]);
    setTags([]);
    setShowLinkInput(false);
    setLinkUrl('');
    setLinkPreview('');
    onClose();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real app, this would upload to a CDN
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments([...attachments, {
          type: 'image',
          url: reader.result as string
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddLink = () => {
    if (linkUrl) {
      setAttachments([...attachments, {
        type: 'link',
        url: linkUrl,
        preview: linkPreview
      }]);
      setLinkUrl('');
      setLinkPreview('');
      setShowLinkInput(false);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleReset}
      title="Create Post"
    >
      <div className="space-y-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What's on your mind?"
          rows={4}
          className={`
            w-full px-4 py-2 rounded-lg border resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        />

        {attachments.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {attachments.map((attachment, index) => (
              <div key={index} className="relative group">
                {attachment.type === 'image' ? (
                  <img
                    src={attachment.url}
                    alt=""
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ) : (
                  <div className={`
                    p-4 rounded-lg border
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600'
                      : 'bg-gray-50 border-gray-200'
                    }
                  `}>
                    <div className={`text-sm mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {new URL(attachment.url).hostname}
                    </div>
                    {attachment.preview && (
                      <div className={`font-medium ${
                        theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {attachment.preview}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(index)}
                  className={`
                    absolute top-2 right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity
                    ${theme === 'dark'
                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <ActionIcon
              icon={<Image className="h-5 w-5" />}
              label="Add Image"
              onClick={() => document.getElementById('image-upload')?.click()}
            />
            <ActionIcon
              icon={<LinkIcon className="h-5 w-5" />}
              label="Add Link"
              onClick={() => setShowLinkInput(true)}
            />
            <ActionIcon
              icon={<Hash className="h-5 w-5" />}
              label="Add Tags"
              onClick={() => document.getElementById('tag-input')?.focus()}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!content.trim()}
            className={`
              px-4 py-2 rounded-lg transition-colors
              ${content.trim()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : theme === 'dark'
                  ? 'bg-gray-700 text-gray-400'
                  : 'bg-gray-100 text-gray-400'
              }
            `}
          >
            Post
          </button>
        </div>

        {showLinkInput && (
          <div className="space-y-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL"
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
            <input
              type="text"
              value={linkPreview}
              onChange={(e) => setLinkPreview(e.target.value)}
              placeholder="Enter link preview text (optional)"
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowLinkInput(false)}
                className={`
                  px-3 py-1 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={!linkUrl}
                className={`
                  px-3 py-1 rounded-lg transition-colors
                  ${linkUrl
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-400'
                      : 'bg-gray-100 text-gray-400'
                  }
                `}
              >
                Add Link
              </button>
            </div>
          </div>
        )}

        <TagInput
          tags={tags}
          onChange={setTags}
        />
      </div>
    </Modal>
  );
}